import { useState, useEffect, useMemo } from "react";
import { GoPlus } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import { TicketStageStepper } from "./TicketStageStepper";
import { SimpleModal } from "../Shared/SimpleModal";
import { SwimlaneDiagram } from "../Shared/SwimlaneDiagram";

function deadlineInfo(deadline) {
  if (!deadline) return null;
  const dl = new Date(deadline);
  if (isNaN(dl.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  dl.setHours(0, 0, 0, 0);
  const diff = Math.floor((dl - now) / 86400000);
  if (diff < 0)
    return { overdue: true, text: `Overdue (${Math.abs(diff)}d)`, cls: "bg-red-50 text-red-600 border-red-200" };
  return { overdue: false, text: `On time (${diff}d left)`, cls: "bg-green-50 text-green-600 border-green-200" };
}

export const TicketListEng = ({
  tickets = [],
  mode,
  setMode,
  user,
  filter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [swimlaneOpen, setSwimlaneOpen] = useState(false);
  const [swimlaneTicket, setSwimlaneTicket] = useState(null);
  const authToken = localStorage.getItem("authToken");

  const filtered = useMemo(() => {
    let list = tickets;
    if (mode === "tasks") list = list.filter((t) => t.assign && t.assign.split("@")[0] === user);
    else if (mode === "tickets") list = list.filter((t) => t.createdBy && t.createdBy.split("@")[0] === user);

    if (startDate || endDate) {
      list = list.filter((t) => {
        const d = new Date(t.createdAt);
        if (isNaN(d.getTime())) return false;
        if (startDate && d < new Date(startDate)) return false;
        if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); if (d > e) return false; }
        return true;
      });
    }

    // "" = Total (all tickets); otherwise filter by the selected status.
    if (filter) {
      list = list.filter((t) => t.stageName === filter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      const matcher = (t, term) => {
        const hay = [
          t.createdBy, t.ticketId, t.assign, t.org, t.requestTypeName, t.stageName, t.title,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(term.trim());
      };
      if (q.includes(" and ")) {
        const terms = q.split(" and ");
        list = list.filter((t) => terms.every((term) => matcher(t, term)));
      } else if (q.includes(" or ")) {
        const terms = q.split(" or ");
        list = list.filter((t) => terms.some((term) => matcher(t, term)));
      } else {
        list = list.filter((t) => matcher(t, q));
      }
    }

    return list;
  }, [tickets, mode, user, filter, startDate, endDate, search]);

  useEffect(() => setPage(1), [search, mode, filter, startDate, endDate, itemsPerPage, tickets]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const pageItems = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  function openSwimlane(t) {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${t.ticketId}/history`,
      { method: "GET", headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` } }
    )
      .then((r) => (r.status === 401 ? null : r.json()))
      .then((data) => {
        if (!data) return;
        setSwimlaneTicket({
          id: t.ticketId,
          currentStage: t.stageName,
          team: t.org || "Default",
          assign: t.assign,
          created: t.createdBy || "System",
          createdDate: t.createdAt,
          history: Array.isArray(data) ? data : [],
        });
        setSwimlaneOpen(true);
      })
      .catch((err) => console.error("Swimlane fetch error:", err));
  }

  const modeBtn = (key, label) => (
    <button
      onClick={() => setMode(key)}
      className={`flex items-center justify-center px-4 py-2 rounded-full transition whitespace-nowrap ${
        mode === key ? "bg-jiojbmpurple text-white" : "text-black"
      }`}
    >
      <p className="tracking-tight font-black">{label}</p>
    </button>
  );

  return (
    <div className="flex flex-col gap-2 p-2 pt-4 rounded-3xl">
      <div className="flex gap-1 m-3 mb-0 items-center justify-center">
        <p className="mr-auto text-2xl font-black tracking-tight leading-snug">
          Tickets
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center mb-2 mt-2 gap-2 flex-wrap">
        <div className="flex gap-1 ml-2 bg-jiojbm20 w-fit rounded-full">
          {modeBtn("tickets", "Raised")}
          {modeBtn("tasks", "Assigned")}
        </div>

        <div className="flex flex-1 justify-end items-center gap-2">
          <input
            type="date"
            className="border border-gray-300 rounded-full px-3 h-9 text-sm text-gray-700 focus:outline-none focus:border-jiojbm50"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="border border-gray-300 rounded-full px-3 h-9 text-sm text-gray-700 focus:outline-none focus:border-jiojbm50"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />

          <div className="flex items-center bg-white rounded-full border h-9 px-2">
            <span className="w-5 h-5 fill-gray-500 ml-2 mr-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
              </svg>
            </span>
            <input
              className="w-40 px-1 text-gray-700 leading-tight tracking-tight focus:outline-none"
              type="text"
              placeholder="Search by any field"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            onClick={() => navigate("/Form")}
            className="flex items-center gap-2 bg-jiojbmpurple px-4 font-black h-9 text-white rounded-full transition whitespace-nowrap"
          >
            <span className="flex items-center justify-center w-5 h-5 bg-white rounded-full">
              <GoPlus className="text-jiojbmpurple text-xl" />
            </span>
            <p className="tracking-tight">New Ticket</p>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto border border-gray-300 rounded-lg m-2">
        <table className="min-w-full bg-white rounded-lg shadow-sm" style={{ fontSize: "14px" }}>
          <thead>
            <tr>
              <th className="py-2 px-4 border-b text-left font-black">Ticket ID</th>
              <th className="py-2 px-4 border-b text-left font-black">Application</th>
              <th className="py-2 px-4 border-b text-left font-black">Request Type</th>
              <th className="py-2 px-4 border-b text-left font-black">Stage</th>
              <th className="py-2 px-4 border-b text-left font-black">Deadline</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((t) => {
              const dlInfo = deadlineInfo(t.deadline);
              return (
                <tr
                  key={t.ticketId || t._id}
                  onDoubleClick={() => navigate(`/Ticket/${t.ticketId}`)}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="py-2 px-4 border-b">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => navigate(`/Ticket/${t.ticketId}`)}
                        className="text-jiojbm60 font-black hover:underline"
                      >
                        {t.ticketId}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openSwimlane(t); }}
                        className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-100 text-gray-400 hover:text-jiojbm60 transition"
                        title="View Swimlane"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" />
                        </svg>
                      </button>
                    </div>
                    {t.title && (
                      <p className="text-xs text-gray-400 truncate max-w-[220px]">
                        {t.title.length > 48 ? t.title.slice(0, 48) + "..." : t.title}
                      </p>
                    )}
                  </td>
                  <td className="py-2 px-4 border-b text-gray-600">{t.org || "—"}</td>
                  <td className="py-2 px-4 border-b text-gray-600">{t.requestTypeName || "—"}</td>
                  <td className="py-2 px-4 border-b">
                    <TicketStageStepper stage={t.stageName} />
                  </td>
                  <td className="py-2 px-4 border-b">
                    <div className="text-gray-600 whitespace-nowrap">{t.deadline || "—"}</div>
                    {dlInfo && (
                      <span className={`inline-block mt-0.5 text-[10px] font-bold border rounded-full px-2 py-0.5 whitespace-nowrap ${dlInfo.cls}`}>
                        {dlInfo.text}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-gray-400 text-sm">No tickets found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center w-full px-2">
        <div className="flex-1 flex gap-16 pl-32 justify-center items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`w-10 h-10 flex items-center justify-center rounded-full ${
              page === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
            }`}
          >
            <div
              className={`relative flex items-center justify-center w-10 h-10 rounded-full cursor-pointer ${
                page === 1 ? "fill-gray60" : "fill-jiojbm50 hover:bg-jiojbm20"
              }`}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                <path d="M14 17a1.003 1.003 0 01-.71-.29l-4-4a1 1 0 010-1.42l4-4a1.005 1.005 0 011.42 1.42L11.41 12l3.3 3.29a.997.997 0 01.219 1.095.999.999 0 01-.93.615z"></path>
              </svg>
            </div>
          </button>

          <p className="text-gray-700">
            Page {page} of {totalPages}
          </p>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`w-10 h-10 flex items-center justify-center rounded-full ${
              page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
            }`}
          >
            <div
              className={`relative flex items-center justify-center rotate-180 w-10 h-10 rounded-full cursor-pointer ${
                page === totalPages ? "fill-gray60" : "fill-jiojbm50 hover:bg-jiojbm20"
              }`}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                <path d="M14 17a1.003 1.003 0 01-.71-.29l-4-4a1 1 0 010-1.42l4-4a1.005 1.005 0 011.42 1.42L11.41 12l3.3 3.29a.997.997 0 01.219 1.095.999.999 0 01-.93.615z"></path>
              </svg>
            </div>
          </button>
        </div>

        <div className="ml-auto flex gap-x-2 items-center">
          <p className="font-bold text-jiojbmpurple tracking-tight leading-tight text-sm whitespace-nowrap">
            Tickets Per Page
          </p>
          <select
            className="border border-gray-300 rounded-full px-3 py-1"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            {Array.from({ length: 10 }, (_, i) => (
              <option key={i} value={5 + i * 5}>
                {5 + i * 5}
              </option>
            ))}
          </select>
        </div>
      </div>

      <SimpleModal
        open={swimlaneOpen}
        title={swimlaneTicket ? `Swimlane — ${swimlaneTicket.id}` : "Swimlane"}
        onClose={() => setSwimlaneOpen(false)}
        maxWidth="max-w-6xl"
        heightClass="max-h-[85vh]"
      >
        {swimlaneTicket && <SwimlaneDiagram ticket={swimlaneTicket} />}
      </SimpleModal>
    </div>
  );
};
